import { useEffect, useState, useRef, useMemo } from "react";

import { usePagination } from "../hooks/usePagination";
import UniversalPagination from "../common/UniversalPagination";
import Sidebar from '../components/Sidebar'
import Header from '../components/Header'

import Swal from "sweetalert2";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import { API_BASE } from "../config/api";
import { useAdminAuth } from "../context/AdminAuthContext";

const emptyUserForm = {
    Usuario_Nombre: "",
    Usuario_Apellido: "",
    Usuario_Telefono: "",
    Usuario_Whatsapp: "",
    Usuario_Nacimiento: "",
    Usuario_Correo: "",
    Usuario_Usuario: "",
    Usuario_Pass_Temp: "",
    Disty_ID: "",
    Rol_ID: "",
    Grado_ID: "",
    Usuario_Activo: 1,
};

function UserList() {
    // CRUD Usuarios
    const [userForm, setUserForm] = useState(emptyUserForm);
    const [selectedUser, setSelectedUser] = useState(null);
    const [savingUser, setSavingUser] = useState(false);
    const [userCrudError, setUserCrudError] = useState("");
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [catalogs, setCatalogs] = useState({
        roles: [],
        distys: [],
        grados: [],
    });

    const { admin } = useAdminAuth();

    const rolId = Number(admin?.rid);
    const isAdminGeneral = rolId === 1;
    const isSuperLider = rolId === 5;

    const rolOptions = useMemo(() => {
        const map = new Map();

        users.forEach((u) => {
            if (u.Rol_ID && u.Rol_Nombre) {
                map.set(u.Rol_ID, u.Rol_Nombre);
            }
        });

        return Array.from(map, ([id, nombre]) => ({
            id,
            nombre,
        }));
    }, [users]);

    const distyOptions = useMemo(() => {
        const map = new Map();

        users.forEach((u) => {
            if (u.Disty_ID && u.Disty_Nombre) {
                map.set(u.Disty_ID, u.Disty_Nombre);
            }
        });

        return Array.from(map, ([id, nombre]) => ({
            id,
            nombre,
        }));
    }, [users]);

    const gradoOptions = useMemo(() => {
        const map = new Map();

        users.forEach((u) => {
            if (u.Grado_ID && u.Grado_Nombre) {
                map.set(u.Grado_ID, u.Grado_Nombre);
            }
        });

        return Array.from(map, ([id, nombre]) => ({
            id,
            nombre,
        }));
    }, [users]);

    

    const handleUserFormChange = (e) => {
    const { name, value } = e.target;

    setUserForm((prev) => ({
        ...prev,
        [name]: value,
    }));
};

const loadUserCatalogs = async () => {
    try {
        const res = await fetch(`${API_BASE}/api/admin/users/catalogs`, {
            method: "GET",
            credentials: "include",
        });

        const data = await res.json();

        if (!res.ok || !data.ok) {
            throw new Error(data.message || "Error al obtener catálogos");
        }

        setCatalogs({
            roles: data.data?.roles || [],
            distys: data.data?.distys || [],
            grados: data.data?.grados || [],
        });
    } catch (error) {
        console.error("Error loadUserCatalogs:", error);
        setCatalogs({
            roles: [],
            distys: [],
            grados: [],
        });
    }
};

const resetUserForm = () => {
    setUserForm(emptyUserForm);
    setSelectedUser(null);
    setUserCrudError("");
};

const openEditUserModal = (user) => {
    console.log("USER EDIT:", user);

    setSelectedUser(user);
    setUserCrudError("");

    setUserForm({
        Usuario_Nombre: user.Usuario_Nombre || "",
        Usuario_Apellido: user.Usuario_Apellido || "",
        Usuario_Telefono: user.Usuario_Telefono || "",
        Usuario_Whatsapp: user.Usuario_Whatsapp || "",
        Usuario_Nacimiento: user.Usuario_Nacimiento
            ? String(user.Usuario_Nacimiento).slice(0, 10)
            : "",
        Usuario_Correo: user.Usuario_Correo || "",
        Usuario_Usuario: user.Usuario_Usuario || "",
        Usuario_Pass_Temp: "",
        Disty_ID: user.Disty_ID != null ? String(user.Disty_ID) : "",
        Rol_ID: user.Rol_ID != null ? String(user.Rol_ID) : "",
        Grado_ID: user.Grado_ID != null ? String(user.Grado_ID) : "",
        Usuario_Activo: user.Usuario_Activo != null ? String(user.Usuario_Activo) : "1",
    });
};

const closeModal = (modalId) => {
    const modalEl = document.getElementById(modalId);

    if (window.bootstrap && modalEl) {
        const modal = window.bootstrap.Modal.getInstance(modalEl);
        modal?.hide();
    }
};

const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
        setSavingUser(true);
        setUserCrudError("");

        const res = await fetch(`${API_BASE}/api/admin/users`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(userForm),
        });

        const data = await res.json();

        if (!res.ok || !data.ok) {
            throw new Error(data.message || "Error al crear usuario");
        }

        await loadUsers();
        resetUserForm();
        closeModal("addUserModal");
    } catch (error) {
        setUserCrudError(error.message);
    } finally {
        setSavingUser(false);
    }
};

const handleUpdateUser = async (e) => {
    e.preventDefault();

    try {
        if (!selectedUser?.Usuario_ID) return;

        setSavingUser(true);
        setUserCrudError("");

        const res = await fetch(`${API_BASE}/api/admin/users/${selectedUser.Usuario_ID}`, {
            method: "PUT",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(userForm),
        });

        const data = await res.json();

        if (!res.ok || !data.ok) {
            throw new Error(data.message || "Error al actualizar usuario");
        }

        await loadUsers();
        resetUserForm();
        closeModal("editUserModal");
    } catch (error) {
        setUserCrudError(error.message);
    } finally {
        setSavingUser(false);
    }
};

const handleDeactivateUser = async (user) => {
    const confirmDelete = window.confirm(
        `¿Seguro que deseas desactivar a ${user.Usuario_Nombre_Completo}?`
    );

    if (!confirmDelete) return;

    try {
        const res = await fetch(`${API_BASE}/api/admin/users/${user.Usuario_ID}/deactivate`, {
            method: "PATCH",
            credentials: "include",
        });

        const data = await res.json();

        if (!res.ok || !data.ok) {
            throw new Error(data.message || "Error al desactivar usuario");
        }

        await loadUsers();
    } catch (error) {
        alert(error.message);
    }
};

    // Carga Masiva
    const [selectedExcelFile, setSelectedExcelFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [importPreview, setImportPreview] = useState(null);
    const [importError, setImportError] = useState("");

    // Helpers
    const formatDate = (dateValue) => {
        if (!dateValue) return "Sin fecha";

        const date = new Date(dateValue);

        if (Number.isNaN(date.getTime())) return "Sin fecha";

        return date.toLocaleDateString("es-MX", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const getInitials = (user) => {
        const name = user.Usuario_Nombre || "";
        const lastName = user.Usuario_Apellido || "";

        return `${name.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "U";
    };

    const getPlanLabel = (planName) => {
        const plan = String(planName || "").toLowerCase();

        if (plan.includes("bronce")) return "🥉 Bronce";
        if (plan.includes("plata")) return "🥈 Plata";
        if (plan.includes("oro") && plan.includes("rola")) return "🥇 Oro Rola";
        if (plan.includes("oro")) return "🥇 Oro";

        return "Sin plan";
    };

    // Funcion para cargar usuarios 
    const loadUsers = async () => {
        try {
            setLoadingUsers(true);

            const res = await fetch(`${API_BASE}/api/admin/users`, {
                method: "GET",
                credentials: "include",
            });

            const data = await res.json();

            if (!res.ok || !data.ok) {
                throw new Error(data.message || "Error al obtener usuarios");
            }

            setUsers(Array.isArray(data.users) ? data.users : []);
        } catch (error) {
            console.error("Error cargando usuarios:", error);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        loadUsers();
        loadUserCatalogs();
    }, []);

    // Funcion para ver detalle usuarios 

    // Funcion para editar usuarios 

    // Funcion para eliminar usuarios 

    // Funcion para carga masiva de usuarios por excel
    const handleDownloadTemplate = () => {
        window.open(`${API_BASE}/api/admin/users/import/template`, "_blank");
    };

    const handleExcelFileChange = (e) => {
        const file = e.target.files?.[0] || null;

        setSelectedExcelFile(file);
        setImportPreview(null);
        setImportError("");
    };

    const handlePreviewImport = async () => {
        try {
            if (!selectedExcelFile) {
                setImportError("Selecciona un archivo Excel antes de validar.");
                return;
            }

            setImportLoading(true);
            setImportError("");

            const formData = new FormData();
            formData.append("file", selectedExcelFile);

            const res = await fetch(`${API_BASE}/api/admin/users/import/preview`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });

            const data = await res.json();

            if (!res.ok || !data.ok) {
                throw new Error(data.message || "Error al validar archivo");
            }

            setImportPreview(data);
        } catch (error) {
            console.error("Error preview import:", error);
            setImportError(error.message || "Error al validar archivo");
        } finally {
            setImportLoading(false);
        }
    };

    const handleConfirmImport = async () => {
        try {
            if (!importPreview?.logId) {
                setImportError("No hay una validación activa para confirmar.");
                return;
            }

            setConfirmLoading(true);
            setImportError("");

            const res = await fetch(`${API_BASE}/api/admin/users/import/confirm`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    logId: importPreview.logId,
                }),
            });

            const data = await res.json();

            if (!res.ok || !data.ok) {
                throw new Error(data.message || "Error al confirmar importación");
            }

            await loadUsers();

            setImportPreview({
                ...importPreview,
                confirmed: true,
                confirmResult: data,
            });
        } catch (error) {
            console.error("Error confirm import:", error);
            setImportError(error.message || "Error al confirmar importación");
        } finally {
            setConfirmLoading(false);
        }
    };

    const handleResetImportModal = () => {
        setSelectedExcelFile(null);
        setImportPreview(null);
        setImportError("");
    };

    // Importacion de Puntos 
    const [pointsExcelFile, setPointsExcelFile] = useState(null);
    const [pointsRegionId, setPointsRegionId] = useState("");
    const [pointsApplyCoins, setPointsApplyCoins] = useState(false);

    const [pointsImportPreview, setPointsImportPreview] = useState(null);
    const [pointsImportResult, setPointsImportResult] = useState(null);
    const [pointsImportError, setPointsImportError] = useState("");

    const [previewingPointsImport, setPreviewingPointsImport] = useState(false);
    const [confirmingPointsImport, setConfirmingPointsImport] = useState(false);
    const [cancelingPointsImport, setCancelingPointsImport] = useState(false);
    const [downloadingPointsTemplate, setDownloadingPointsTemplate] = useState(false);

const handleResetPointsImportModal = () => {
    setPointsExcelFile(null);
    setPointsRegionId("");
    setPointsApplyCoins(false);

    setPointsImportPreview(null);
    setPointsImportResult(null);
    setPointsImportError("");

    setPreviewingPointsImport(false);
    setConfirmingPointsImport(false);
    setCancelingPointsImport(false);
};

const handleDownloadPointsTemplate = async () => {
    try {
        setDownloadingPointsTemplate(true);
        setPointsImportError("");

        const response = await fetch(
            `${API_BASE}/api/admin/puntos-extra/import/template`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        if (!response.ok) {
            throw new Error("No se pudo descargar la plantilla");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "plantilla_puntos_extra.xlsx";
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error al descargar plantilla:", error);
        setPointsImportError(error.message || "Error al descargar plantilla");
    } finally {
        setDownloadingPointsTemplate(false);
    }
};

const handlePreviewPointsImport = async () => {
    try {
        setPointsImportError("");
        setPointsImportResult(null);

        if (!pointsExcelFile) {
            setPointsImportError("Selecciona un archivo Excel");
            return;
        }

        if (!pointsRegionId) {
            setPointsImportError("Selecciona una región");
            return;
        }

        setPreviewingPointsImport(true);

        const formData = new FormData();
        formData.append("file", pointsExcelFile);
        formData.append("regionId", pointsRegionId);
        formData.append("aplicaCoins", pointsApplyCoins ? "true" : "false");

        const response = await fetch(
            `${API_BASE}/api/admin/puntos-extra/import/preview`,
            {
                method: "POST",
                credentials: "include",
                body: formData,
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(result.message || "Error al generar preview");
        }

        setPointsImportPreview(result.data);

        // Swal.fire({
        //     icon: "success",
        //     title: "Preview generado",
        //     text: `Filas válidas: ${result.data.validRows}. Filas con error: ${result.data.errorRows}.`,
        //     timer: 2200,
        //     showConfirmButton: false,
        // });
    } catch (error) {
        console.error("Error preview puntos extra:", error);
        setPointsImportError(error.message || "Error al generar preview");
    } finally {
        setPreviewingPointsImport(false);
    }
};

const handleConfirmPointsImport = async () => {
    try {
        setPointsImportError("");
        setPointsImportResult(null);

        if (!pointsImportPreview?.logId) {
            setPointsImportError("No hay preview pendiente por confirmar");
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "¿Confirmar importación?",
            text: "Se insertarán los puntos de las filas válidas. Las filas con error serán omitidas.",
            showCancelButton: true,
            confirmButtonText: "Sí, confirmar",
            cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        setConfirmingPointsImport(true);

        const response = await fetch(
            `${API_BASE}/api/admin/puntos-extra/import/${pointsImportPreview.logId}/confirm`,
            {
                method: "POST",
                credentials: "include",
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(result.message || "Error al confirmar importación");
        }

        setPointsImportResult(result.data);

        // Swal.fire({
        //     icon: "success",
        //     title: "Importación confirmada",
        //     html: `
        //         <div class="text-start">
        //             <p><b>Puntos insertados:</b> ${result.data.puntosInsertados}</p>
        //             <p><b>Coins insertados:</b> ${result.data.creditosInsertados}</p>
        //             <p><b>Errores omitidos:</b> ${result.data.errores}</p>
        //         </div>
        //     `,
        // });
    } catch (error) {
        console.error("Error confirmar puntos extra:", error);
        setPointsImportError(error.message || "Error al confirmar importación");
    } finally {
        setConfirmingPointsImport(false);
    }
};

const handleCancelPointsImport = async () => {
    try {
        setPointsImportError("");

        if (!pointsImportPreview?.logId) {
            setPointsImportError("No hay preview pendiente por cancelar");
            return;
        }

        const confirm = await Swal.fire({
            icon: "warning",
            title: "¿Cancelar preview?",
            text: "La importación quedará marcada como cancelada y no se insertarán puntos.",
            showCancelButton: true,
            confirmButtonText: "Sí, cancelar",
            cancelButtonText: "Regresar",
        });

        if (!confirm.isConfirmed) return;

        setCancelingPointsImport(true);

        const response = await fetch(
            `${API_BASE}/api/admin/puntos-extra/import/${pointsImportPreview.logId}/cancel`,
            {
                method: "POST",
                credentials: "include",
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(result.message || "Error al cancelar importación");
        }

        Swal.fire({
            icon: "success",
            title: "Importación cancelada",
            timer: 1800,
            showConfirmButton: false,
        });

        handleResetPointsImportModal();
    } catch (error) {
        console.error("Error cancelar puntos extra:", error);
        setPointsImportError(error.message || "Error al cancelar importación");
    } finally {
        setCancelingPointsImport(false);
    }
};

// Filtros
const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return users;

    return users.filter((user) => {
        const fullName = `${user.Usuario_Nombre || ""} ${user.Usuario_Apellido || ""}`.toLowerCase();

        return (
            fullName.includes(term) ||
            String(user.Usuario_Correo || "").toLowerCase().includes(term) ||
            String(user.Usuario_Whatsapp || "").toLowerCase().includes(term) ||
            String(user.Disty_Nombre || "").toLowerCase().includes(term) ||
            String(user.Rol_Nombre || "").toLowerCase().includes(term) ||
            String(user.Plan_Nombre || "").toLowerCase().includes(term) ||
            String(user.Grado_Nombre || "").toLowerCase().includes(term)
        );
    });
}, [users, searchTerm]);

    // Paginador
    const {
        currentPage,
        totalPages,
        totalItems,
        paginatedItems,
        visiblePages,
        startItem,
        endItem,
        goToPage,
        goToPreviousPage,
        goToNextPage,
        resetPagination,
        hasPreviousPage,
        hasNextPage,
    } = usePagination(filteredUsers, 25, 5);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        resetPagination();
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        resetPagination();
    };

    return (
        <>
        <Sidebar/>
        <main class="main-content">
            <Header/>
            <div class="content-inner container-fluid pb-0" id="page_layout">
                <div class="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                    <div class="d-flex flex-column">
                        <h3>Lista de Usuarios</h3>
                        <p class="text-primary mb-0">Panduit Core</p>
                    </div>

                    {isAdminGeneral && (
                    <div className="col-md-6 gap-2 d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-warning btn-sm"
                            data-bs-toggle="modal" data-bs-target="#addPuntosModal"
                        >
                            <svg width="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight:'5px'}}>
                                <path opacity="0.4" d="M16.6667 2H7.33333C3.92889 2 2 3.92889 2 7.33333V16.6667C2 20.0622 3.92 22 7.33333 22H16.6667C20.0711 22 22 20.0622 22 16.6667V7.33333C22 3.92889 20.0711 2 16.6667 2Z" fill="currentColor"></path>
                                <path d="M15.3205 12.7083H12.7495V15.257C12.7495 15.6673 12.4139 16 12 16C11.5861 16 11.2505 15.6673 11.2505 15.257V12.7083H8.67955C8.29342 12.6687 8 12.3461 8 11.9613C8 11.5765 8.29342 11.2539 8.67955 11.2143H11.2424V8.67365C11.2824 8.29088 11.6078 8 11.996 8C12.3842 8 12.7095 8.29088 12.7495 8.67365V11.2143H15.3205C15.7066 11.2539 16 11.5765 16 11.9613C16 12.3461 15.7066 12.6687 15.3205 12.7083Z" fill="currentColor"></path>
                            </svg>                        
                            Carga Masiva de Puntos por Excel
                        </button>

                        <button
                            type="button"
                            className="btn btn-success btn-sm"
                            data-bs-toggle="modal" data-bs-target="#addUsersModal"
                        >
                            <svg width="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight:'5px'}}>
                                <path opacity="0.4" d="M16.6667 2H7.33333C3.92889 2 2 3.92889 2 7.33333V16.6667C2 20.0622 3.92 22 7.33333 22H16.6667C20.0711 22 22 20.0622 22 16.6667V7.33333C22 3.92889 20.0711 2 16.6667 2Z" fill="currentColor"></path>
                                <path d="M15.3205 12.7083H12.7495V15.257C12.7495 15.6673 12.4139 16 12 16C11.5861 16 11.2505 15.6673 11.2505 15.257V12.7083H8.67955C8.29342 12.6687 8 12.3461 8 11.9613C8 11.5765 8.29342 11.2539 8.67955 11.2143H11.2424V8.67365C11.2824 8.29088 11.6078 8 11.996 8C12.3842 8 12.7095 8.29088 12.7495 8.67365V11.2143H15.3205C15.7066 11.2539 16 11.5765 16 11.9613C16 12.3461 15.7066 12.6687 15.3205 12.7083Z" fill="currentColor"></path>
                            </svg>                        
                            Carga Masiva por Excel
                        </button>
                    </div>
                    )}
                </div>

                <div class="row">
                    <div class="col-sm-12">
                        <div class="card">
                            <div class="card-header d-flex justify-content-end gap-3 flex-wrap">
                                <div className="row g-2 align-items-end w-100">
                                    <div className="col-md-4">
                                        <label className="form-label mb-1">Buscar</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Buscar por nombre, correo, WhatsApp o disty..."
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>

                                    <div className="col-md-4 d-flex justify-content-end gap-2 ms-auto">

                                        <button
                                            type="button"
                                            className="btn btn-secondary-subtle btn-sm"
                                            title="Limpiar búsqueda"
                                            onClick={handleClearFilters}
                                        >
                                            <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <g opacity="0.4">
                                                <path
                                                    d="M4.88076 14.6713C4.74978 14.2784 4.32504 14.066 3.93208 14.197C3.53912 14.328 3.32675 14.7527 3.45774 15.1457L4.88076 14.6713ZM20.8808 15.1457C21.0117 14.7527 20.7994 14.328 20.4064 14.197C20.0135 14.066 19.5887 14.2784 19.4577 14.6713L20.8808 15.1457ZM4.16925 14.9085C3.45774 15.1457 3.45785 15.146 3.45797 15.1464C3.45802 15.1465 3.45815 15.1469 3.45825 15.1472C3.45845 15.1478 3.45868 15.1485 3.45895 15.1493C3.45948 15.1509 3.46013 15.1528 3.46092 15.1551C3.46249 15.1597 3.46456 15.1657 3.46716 15.1731C3.47235 15.188 3.47961 15.2084 3.48902 15.2341C3.50782 15.2854 3.53521 15.3576 3.5717 15.4477C3.64461 15.6279 3.7542 15.8805 3.90468 16.1814C4.2048 16.7817 4.67223 17.5836 5.34308 18.3886C6.68942 20.0043 8.88343 21.6585 12.1693 21.6585V20.1585C9.45507 20.1585 7.64908 18.8128 6.49542 17.4284C5.91627 16.7334 5.5087 16.0354 5.24632 15.5106C5.11555 15.2491 5.02201 15.0329 4.96212 14.8849C4.9322 14.811 4.91076 14.7543 4.89733 14.7177C4.89062 14.6994 4.88593 14.6861 4.88318 14.6783C4.88181 14.6744 4.88093 14.6718 4.88053 14.6706C4.88033 14.67 4.88025 14.6698 4.88029 14.6699C4.88031 14.67 4.88036 14.6701 4.88044 14.6704C4.88047 14.6705 4.88056 14.6707 4.88058 14.6708C4.88067 14.671 4.88076 14.6713 4.16925 14.9085ZM12.1693 21.6585C15.4551 21.6585 17.6491 20.0043 18.9954 18.3886C19.6663 17.5836 20.1337 16.7817 20.4338 16.1814C20.5843 15.8805 20.6939 15.6279 20.7668 15.4477C20.8033 15.3576 20.8307 15.2854 20.8495 15.2341C20.8589 15.2084 20.8662 15.188 20.8713 15.1731C20.8739 15.1657 20.876 15.1597 20.8776 15.1551C20.8784 15.1528 20.879 15.1509 20.8796 15.1493C20.8798 15.1485 20.8801 15.1478 20.8803 15.1472C20.8804 15.1469 20.8805 15.1465 20.8805 15.1464C20.8807 15.146 20.8808 15.1457 20.1693 14.9085C19.4577 14.6713 19.4578 14.671 19.4579 14.6708C19.4579 14.6707 19.458 14.6705 19.4581 14.6704C19.4581 14.6701 19.4582 14.67 19.4582 14.6699C19.4583 14.6698 19.4582 14.67 19.458 14.6706C19.4576 14.6718 19.4567 14.6744 19.4553 14.6783C19.4526 14.6861 19.4479 14.6994 19.4412 14.7177C19.4277 14.7543 19.4063 14.811 19.3764 14.8849C19.3165 15.0329 19.223 15.2491 19.0922 15.5106C18.8298 16.0354 18.4222 16.7334 17.8431 17.4284C16.6894 18.8128 14.8834 20.1585 12.1693 20.1585V21.6585Z"
                                                    fill="currentColor"></path>
                                                <path d="M21.5183 19.2271C21.4293 19.2234 21.3427 19.196 21.2671 19.1465L16.3546 15.8924C16.2197 15.8026 16.1413 15.6537 16.148 15.4969C16.1546 15.34 16.2452 15.1982 16.3873 15.1202L21.5571 12.2926C21.7075 12.2106 21.8932 12.213 22.0416 12.3003C22.1907 12.387 22.2783 12.5436 22.2712 12.7096L22.014 18.7913C22.007 18.9573 21.9065 19.1059 21.7506 19.1797C21.6772 19.215 21.597 19.2305 21.5183 19.2271" fill="currentColor"></path>
                                                </g>
                                                <path
                                                d="M20.0742 10.0265C20.1886 10.4246 20.6041 10.6546 21.0022 10.5401C21.4003 10.4257 21.6302 10.0102 21.5158 9.61214L20.0742 10.0265ZM4.10803 8.88317C3.96071 9.27031 4.15513 9.70356 4.54226 9.85087C4.92939 9.99818 5.36265 9.80377 5.50996 9.41664L4.10803 8.88317ZM20.795 9.81934C21.5158 9.61214 21.5157 9.6118 21.5156 9.61144C21.5155 9.61129 21.5154 9.6109 21.5153 9.61059C21.5152 9.60998 21.515 9.60928 21.5147 9.60848C21.5143 9.60689 21.5137 9.60493 21.513 9.6026C21.5116 9.59795 21.5098 9.59184 21.5075 9.58431C21.503 9.56925 21.4966 9.54853 21.4882 9.52251C21.4716 9.47048 21.4473 9.39719 21.4146 9.3056C21.3493 9.12256 21.2503 8.8656 21.1126 8.55861C20.8378 7.94634 20.4044 7.12552 19.7678 6.29313C18.4902 4.62261 16.3673 2.87801 13.0844 2.74053L13.0216 4.23922C15.7334 4.35278 17.4816 5.77291 18.5763 7.20436C19.1258 7.92295 19.5038 8.63743 19.744 9.17271C19.8638 9.43949 19.9482 9.65937 20.0018 9.80972C20.0286 9.88483 20.0477 9.94238 20.0596 9.97951C20.0655 9.99808 20.0696 10.0115 20.072 10.0195C20.0732 10.0235 20.074 10.0261 20.0744 10.0273C20.0746 10.0278 20.0746 10.0281 20.0746 10.028C20.0746 10.0279 20.0745 10.0278 20.0745 10.0275C20.0744 10.0274 20.0744 10.0272 20.0743 10.0271C20.0743 10.0268 20.0742 10.0265 20.795 9.81934ZM13.0844 2.74053C9.80146 2.60306 7.54016 4.16407 6.12741 5.72193C5.42345 6.49818 4.92288 7.27989 4.59791 7.86704C4.43497 8.16144 4.31491 8.40923 4.23452 8.58617C4.1943 8.67471 4.16391 8.7457 4.14298 8.79616C4.13251 8.82139 4.1244 8.84151 4.11859 8.85613C4.11568 8.86344 4.11336 8.86938 4.1116 8.8739C4.11072 8.87616 4.10998 8.87807 4.10939 8.87962C4.10909 8.88039 4.10883 8.88108 4.1086 8.88167C4.10849 8.88196 4.10834 8.88234 4.10829 8.88249C4.10815 8.88284 4.10803 8.88317 4.80899 9.14991C5.50996 9.41664 5.50985 9.41692 5.50975 9.41719C5.50973 9.41725 5.50964 9.41749 5.50959 9.4176C5.5095 9.41784 5.50945 9.41798 5.50942 9.41804C5.50938 9.41816 5.50947 9.41792 5.50969 9.41734C5.51014 9.41619 5.51113 9.41365 5.51267 9.40979C5.51574 9.40206 5.52099 9.38901 5.52846 9.37101C5.5434 9.335 5.56719 9.27924 5.60018 9.20664C5.66621 9.0613 5.76871 8.84925 5.91031 8.59341C6.19442 8.08008 6.63084 7.39971 7.23855 6.72958C8.44912 5.39466 10.3098 4.12566 13.0216 4.23922L13.0844 2.74053Z"
                                                fill="currentColor"></path>
                                                <path d="M8.78337 9.33604C8.72981 9.40713 8.65805 9.46292 8.57443 9.49703L3.1072 11.6951C2.95672 11.7552 2.78966 11.7352 2.66427 11.6407C2.53887 11.5462 2.47359 11.3912 2.48993 11.2299L3.09576 5.36863C3.11367 5.19823 3.22102 5.04666 3.37711 4.97402C3.5331 4.9005 3.71173 4.91728 3.84442 5.01726L8.70581 8.68052C8.8385 8.78051 8.90387 8.94759 8.8762 9.1178C8.86358 9.19825 8.83082 9.27308 8.78337 9.33604" fill="currentColor"></path>
                                            </svg>
                                        </button>

                                        <button class="btn btn-primary btn-sm" data-bs-toggle="modal" data-bs-target="#addUserModal" onClick={resetUserForm}>
                                            <span class="btn-inner mr-5">
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-24"><path d="M12 4V20M20 12H4" stroke="currentColor"strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                                            </span>
                                            Agregar
                                        </button>

                                    </div>
                                </div>
                            </div>

                            <div class="card-body px-0">
                                <div class="table-responsive">
                                    <table id="user-list-table" class="table table-striped" role="grid" data-toggle="data-table">
                                        <thead>
                                            <tr class="ligth">
                                            <th>Perfil</th>
                                            <th>Contacto</th>
                                            <th>Agregado por</th>
                                            <th>Disty</th>
                                            <th>Registro</th>
                                            <th>Estado</th>
                                            <th>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                                {loadingUsers ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4">
                                                            <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                                                                <div className="col-md-12 col-sm-4 mb-4">
                                                                    <div className="iq-loader-box">
                                                                        <div className="iq-loader-1"></div>
                                                                    </div>
                                                                </div>

                                                                <span className="text-muted">
                                                                    Cargando usuarios...
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : paginatedItems.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4">
                                                            No se encontraron usuarios.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    paginatedItems.map((user) => (
                                                        <tr key={user.Usuario_ID}>
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center">
                                                                    {user.Usuario_Foto ? (
                                                                        <></>
                                                                        // <img
                                                                        //     className="rounded-circle bg-primary-subtle p-1 me-3"
                                                                        //     alt={user.Usuario_Nombre_Completo || user.Usuario_Nombre}
                                                                        //     loading="lazy"
                                                                        //     style={{ width: "65px", height: "65px", objectFit: "cover" }}
                                                                        //     src={user.Usuario_Foto}
                                                                        // />
                                                                    ) : (
                                                                        // <div
                                                                        //     className="rounded-circle bg-primary-subtle p-1 me-3 d-flex align-items-center justify-content-center fw-bold text-primary"
                                                                        //     style={{ width: "65px", height: "65px", objectFit: "cover" }}
                                                                        // >
                                                                        //     {getInitials(user)}
                                                                        // </div>
                                                                        <></>
                                                                    )}

                                                                    <div className="media-support-info" style={{ textAlign: "initial" }}>
                                                                        <h5 className="iq-sub-label mb-0">
                                                                            {user.Usuario_Nombre || ""} {user.Usuario_Apellido || ""}
                                                                        </h5>

                                                                        {["Admin", "Lider", "Superlider"].includes(user.Rol_Nombre) && (
                                                                            <span className="badge bg-secondary-subtle p-2 text-secondary me-1">
                                                                                {user.Rol_Nombre}
                                                                            </span>
                                                                        )}

                                                                        <span className="badge bg-light-subtle p-2 text-dark me-1">
                                                                            {getPlanLabel(user.Plan_Nombre)} - {user.Grado_Nombre || "Sin grado"}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <div className="media-support-info" style={{ textAlign: "initial" }}>
                                                                    <p className="iq-sub-label mb-0">
                                                                        {user.Usuario_Correo || "Sin correo"}
                                                                    </p>

                                                                    {user.Usuario_Whatsapp ? (
                                                                        <a
                                                                            href={`https://wa.me/${String(user.Usuario_Whatsapp).replace(/\D/g, "")}`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            style={{ color: "#075e54", fontSize: "12px", fontWeight: "600" }}
                                                                        >
                                                                            {user.Usuario_Whatsapp}
                                                                        </a>
                                                                    ) : (
                                                                        <span className="text-muted" style={{ fontSize: "12px" }}>
                                                                            Sin WhatsApp
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            <td>
                                                            {user.Creador_Nombre_Completo ? (
                                                                <>
                                                                    <p className="iq-sub-label mb-0 text-black" style={{fontWeight:'500'}}>Agregado por:</p>
                                                                    <span>
                                                                        {user.Creador_Rol_Nombre || "Usuario"}{" "}
                                                                        {user.Creador_Nombre_Completo}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <span className="text-muted">Sin registro</span>
                                                            )}
                                                        </td>

                                                            <td>{user.Disty_Nombre || "Sin disty"}</td>

                                                            <td>{formatDate(user.Usuario_FechaRegistro)}</td>

                                                            <td>
                                                                {Number(user.Usuario_Activo) === 1 ? (
                                                                    <span className="badge bg-success-subtle p-2 text-success">
                                                                        Activo
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-danger-subtle p-2 text-danger">
                                                                        Inactivo
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <div className="d-flex">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-icon btn-info-subtle ms-2"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#viewUserModal"
                                                                    >
                                                                        <span className="btn-inner">
                                                                            <svg
                                                                                className="icon-32"
                                                                                width="32"
                                                                                viewBox="0 0 24 24"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    opacity="0.4"
                                                                                    fillRule="evenodd"
                                                                                    clipRule="evenodd"
                                                                                    d="M17.7366 6.04606C19.4439 7.36388 20.8976 9.29455 21.9415 11.7091C22.0195 11.8924 22.0195 12.1067 21.9415 12.2812C19.8537 17.1103 16.1366 20 12 20H11.9902C7.86341 20 4.14634 17.1103 2.05854 12.2812C1.98049 12.1067 1.98049 11.8924 2.05854 11.7091C4.14634 6.87903 7.86341 4 11.9902 4H12C14.0683 4 16.0293 4.71758 17.7366 6.04606ZM8.09756 12C8.09756 14.1333 9.8439 15.8691 12 15.8691C14.1463 15.8691 15.8927 14.1333 15.8927 12C15.8927 9.85697 14.1463 8.12121 12 8.12121C9.8439 8.12121 8.09756 9.85697 8.09756 12Z"
                                                                                    fill="currentColor"
                                                                                />
                                                                                <path
                                                                                    d="M14.4308 11.997C14.4308 13.3255 13.3381 14.4115 12.0015 14.4115C10.6552 14.4115 9.5625 13.3255 9.5625 11.997C9.5625 11.8321 9.58201 11.678 9.61128 11.5228H9.66006C10.743 11.5228 11.621 10.6695 11.6601 9.60184C11.7674 9.58342 11.8845 9.57275 12.0015 9.57275C13.3381 9.57275 14.4308 10.6588 14.4308 11.997Z"
                                                                                    fill="currentColor"
                                                                                />
                                                                            </svg>
                                                                        </span>
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-icon btn-warning-subtle  ms-2"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#editUserModal"
                                                                        onClick={() => openEditUserModal(user)}
                                                                    >
                                                                        <span className="btn-inner">
                                                                            <svg
                                                                                className="icon-32"
                                                                                width="32"
                                                                                viewBox="0 0 24 24"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    opacity="0.4"
                                                                                    d="M19.9927 18.9534H14.2984C13.7429 18.9534 13.291 19.4124 13.291 19.9767C13.291 20.5422 13.7429 21.0001 14.2984 21.0001H19.9927C20.5483 21.0001 21.0001 20.5422 21.0001 19.9767C21.0001 19.4124 20.5483 18.9534 19.9927 18.9534Z"
                                                                                    fill="currentColor"
                                                                                ></path>
                                                                                <path
                                                                                    d="M10.309 6.90385L15.7049 11.2639C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8344 7.72908 20.8452L4.23696 20.8882C4.05071 20.8903 3.88775 20.7613 3.84542 20.5764L3.05175 17.1258C2.91419 16.4915 3.05175 15.8358 3.45388 15.3306L9.88256 6.95545C9.98627 6.82108 10.1778 6.79743 10.309 6.90385Z"
                                                                                    fill="currentColor"
                                                                                ></path>
                                                                                <path
                                                                                    opacity="0.4"
                                                                                    d="M18.1208 8.66544L17.0806 9.96401C16.9758 10.0962 16.7874 10.1177 16.6573 10.0124C15.3927 8.98901 12.1545 6.36285 11.2561 5.63509C11.1249 5.52759 11.1069 5.33625 11.2127 5.20295L12.2159 3.95706C13.126 2.78534 14.7133 2.67784 15.9938 3.69906L17.4647 4.87078C18.0679 5.34377 18.47 5.96726 18.6076 6.62299C18.7663 7.3443 18.597 8.0527 18.1208 8.66544Z"
                                                                                    fill="currentColor"
                                                                                ></path>
                                                                            </svg>
                                                                        </span>
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-icon btn-danger-subtle ms-2"
                                                                        onClick={() => handleDeactivateUser(user)}
                                                                    >
                                                                        <span className="btn-inner">
                                                                            <svg
                                                                                className="icon-32"
                                                                                width="32"
                                                                                viewBox="0 0 24 24"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    opacity="0.4"
                                                                                    d="M19.643 9.48851C19.643 9.5565 19.11 16.2973 18.8056 19.1342C18.615 20.8751 17.4927 21.9311 15.8092 21.9611C14.5157 21.9901 13.2494 22.0001 12.0036 22.0001C10.6809 22.0001 9.38741 21.9901 8.13185 21.9611C6.50477 21.9221 5.38147 20.8451 5.20057 19.1342C4.88741 16.2873 4.36418 9.5565 4.35445 9.48851C4.34473 9.28351 4.41086 9.08852 4.54507 8.93053C4.67734 8.78453 4.86796 8.69653 5.06831 8.69653H18.9388C19.1382 8.69653 19.3191 8.78453 19.4621 8.93053C19.5953 9.08852 19.6624 9.28351 19.643 9.48851Z"
                                                                                    fill="currentColor"
                                                                                ></path>
                                                                                <path
                                                                                    d="M21 5.97686C21 5.56588 20.6761 5.24389 20.2871 5.24389H17.3714C16.7781 5.24389 16.2627 4.8219 16.1304 4.22692L15.967 3.49795C15.7385 2.61698 14.9498 2 14.0647 2H9.93624C9.0415 2 8.26054 2.61698 8.02323 3.54595L7.87054 4.22792C7.7373 4.8219 7.22185 5.24389 6.62957 5.24389H3.71385C3.32386 5.24389 3 5.56588 3 5.97686V6.35685C3 6.75783 3.32386 7.08982 3.71385 7.08982H20.2871C20.6761 7.08982 21 6.75783 21 6.35685V5.97686Z"
                                                                                    fill="currentColor"
                                                                                ></path>
                                                                            </svg>
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                    </table>
                                </div>
                            </div>
                            <UniversalPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                startItem={startItem}
                                endItem={endItem}
                                visiblePages={visiblePages}
                                onPageChange={goToPage}
                                onPrevious={goToPreviousPage}
                                onNext={goToNextPage}
                                hasPreviousPage={hasPreviousPage}
                                hasNextPage={hasNextPage}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>

        {/* Modal para crear Usuario */}
        <div className="modal fade" id="addUserModal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <form onSubmit={handleCreateUser}>
                        <div className="modal-header">
                            <h5 className="modal-title">Agregar usuario</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>

                        <div className="modal-body">
                            {userCrudError && (
                                <div className="alert alert-danger">{userCrudError}</div>
                            )}

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Nombre</label>
                                    <input name="Usuario_Nombre" className="form-control" value={userForm.Usuario_Nombre} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Apellido</label>
                                    <input name="Usuario_Apellido" className="form-control" value={userForm.Usuario_Apellido} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Correo</label>
                                    <input type="email" name="Usuario_Correo" className="form-control" value={userForm.Usuario_Correo} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Usuario</label>
                                    <input name="Usuario_Usuario" className="form-control" value={userForm.Usuario_Usuario} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Teléfono</label>
                                    <input name="Usuario_Telefono" className="form-control" value={userForm.Usuario_Telefono} onChange={handleUserFormChange} />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">WhatsApp</label>
                                    <input name="Usuario_Whatsapp" className="form-control" value={userForm.Usuario_Whatsapp} onChange={handleUserFormChange} />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Rol</label>
                                    <select
                                        name="Rol_ID"
                                        className="form-select"
                                        value={userForm.Rol_ID}
                                        onChange={handleUserFormChange}
                                        required
                                    >
                                        <option value="">Seleccionar rol</option>

                                        {catalogs.roles.map((rol) => (
                                            <option key={rol.Rol_ID} value={rol.Rol_ID}>
                                                {rol.Rol_Nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Distribuidor</label>
                                    <select
                                        name="Disty_ID"
                                        className="form-select"
                                        value={userForm.Disty_ID}
                                        onChange={handleUserFormChange}
                                        required
                                    >
                                        <option value="">Seleccionar distribuidor</option>

                                        {catalogs.distys.map((disty) => (
                                            <option key={disty.Disty_ID} value={disty.Disty_ID}>
                                                {disty.Disty_Nombre}
                                                {disty.Plan_Nombre ? ` - ${disty.Plan_Nombre}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Grado</label>
                                    <select
                                        name="Grado_ID"
                                        className="form-select"
                                        value={userForm.Grado_ID}
                                        onChange={handleUserFormChange}
                                        required
                                    >
                                        <option value="">Seleccionar grado</option>

                                        {catalogs.grados.map((grado) => (
                                            <option key={grado.Grado_ID} value={grado.Grado_ID}>
                                                {grado.Grado_Nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Contraseña temporal</label>
                                    <input name="Usuario_Pass_Temp" className="form-control" value={userForm.Usuario_Pass_Temp} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Estado</label>
                                    <select name="Usuario_Activo" className="form-select" value={userForm.Usuario_Activo} onChange={handleUserFormChange}>
                                        <option value={1}>Activo</option>
                                        <option value={0}>Inactivo</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={savingUser}>
                                {savingUser ? "Guardando..." : "Guardar usuario"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {/* Modal para editar Usuario */}
        <div className="modal fade" id="editUserModal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <form onSubmit={handleUpdateUser}>
                        <div className="modal-header">
                            <h5 className="modal-title">Editar usuario</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>

                        <div className="modal-body">
                            {userCrudError && (
                                <div className="alert alert-danger">{userCrudError}</div>
                            )}

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Nombre</label>
                                    <input name="Usuario_Nombre" className="form-control" value={userForm.Usuario_Nombre} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Apellido</label>
                                    <input name="Usuario_Apellido" className="form-control" value={userForm.Usuario_Apellido} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Correo</label>
                                    <input type="email" name="Usuario_Correo" className="form-control" value={userForm.Usuario_Correo} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Usuario</label>
                                    <input name="Usuario_Usuario" className="form-control" value={userForm.Usuario_Usuario} onChange={handleUserFormChange} required />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Teléfono</label>
                                    <input name="Usuario_Telefono" className="form-control" value={userForm.Usuario_Telefono} onChange={handleUserFormChange} />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">WhatsApp</label>
                                    <input name="Usuario_Whatsapp" className="form-control" value={userForm.Usuario_Whatsapp} onChange={handleUserFormChange} />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Rol</label>
                                    <select
                                        name="Rol_ID"
                                        className="form-select"
                                        value={userForm.Rol_ID}
                                        onChange={handleUserFormChange}
                                        required
                                    >
                                        <option value="">Seleccionar rol</option>

                                        {catalogs.roles.map((rol) => (
                                            <option key={rol.Rol_ID} value={String(rol.Rol_ID)}>
    {rol.Rol_Nombre}
</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Distribuidor</label>
                                    <select
                                        name="Disty_ID"
                                        className="form-select"
                                        value={userForm.Disty_ID}
                                        onChange={handleUserFormChange}
                                        required
                                    >
                                        <option value="">Seleccionar distribuidor</option>

                                        {catalogs.distys.map((disty) => (
                                            <option key={disty.Disty_ID} value={String(disty.Disty_ID)}>
    {disty.Disty_Nombre}
    {disty.Plan_Nombre ? ` - ${disty.Plan_Nombre}` : ""}
</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Grado</label>
                                    <select
                                        name="Grado_ID"
                                        className="form-select"
                                        value={userForm.Grado_ID}
                                        onChange={handleUserFormChange}
                                        required
                                    >
                                        <option value="">Seleccionar grado</option>

                                        {catalogs.grados.map((grado) => (
                                            <option key={grado.Grado_ID} value={String(grado.Grado_ID)}>
    {grado.Grado_Nombre}
</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Nueva contraseña temporal</label>
                                    <input name="Usuario_Pass_Temp" className="form-control" value={userForm.Usuario_Pass_Temp} onChange={handleUserFormChange} placeholder="Dejar vacío para no cambiar" />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Estado</label>
                                    <select name="Usuario_Activo" className="form-select" value={userForm.Usuario_Activo} onChange={handleUserFormChange}>
                                        <option value={1}>Activo</option>
                                        <option value={0}>Inactivo</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-warning" disabled={savingUser}>
                                {savingUser ? "Actualizando..." : "Actualizar usuario"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {/* Modal para carga Masiva */}
        <div
            className="modal fade"
            id="addUsersModal"
            tabIndex="-1"
            aria-labelledby="addUsersModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-fullscreen">
                <div className="modal-content">
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title" id="addUsersModalLabel">
                                Carga Masiva de Usuarios por Excel
                            </h5>
                            <small className="text-muted">
                                Sube una plantilla, valida los registros y confirma la importación.
                            </small>
                        </div>

                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleResetImportModal}
                        ></button>
                    </div>

                    <div className="modal-body">
                        {importError && (
                            <div className="alert alert-danger d-flex justify-content-between align-items-center">
                                <span>{importError}</span>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setImportError("")}
                                ></button>
                            </div>
                        )}

                        <div className="row g-3 mb-4">
                            <div className="col-lg-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-2">1. Descargar plantilla</h6>
                                        <p className="text-muted mb-3">
                                            Descarga el Excel con las columnas obligatorias.
                                        </p>

                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={handleDownloadTemplate}
                                        >
                                            Descargar plantilla
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-2">2. Subir archivo</h6>
                                        <p className="text-muted mb-3">
                                            Selecciona el Excel con los usuarios a cargar.
                                        </p>

                                        <input
                                            type="file"
                                            className="form-control form-control-sm"
                                            accept=".xlsx,.xls"
                                            onChange={handleExcelFileChange}
                                        />

                                        {selectedExcelFile && (
                                            <small className="text-success d-block mt-2">
                                                Archivo seleccionado: {selectedExcelFile.name}
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-2">3. Validar información</h6>
                                        <p className="text-muted mb-3">
                                            Genera el preview antes de insertar o actualizar.
                                        </p>

                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            onClick={handlePreviewImport}
                                            disabled={importLoading || !selectedExcelFile}
                                        >
                                            {importLoading ? "Validando..." : "Validar Excel"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {importPreview && (
                            <>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4>{importPreview.totalRows}</h4>
                                                <small>Total filas</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-success">{importPreview.validRows}</h4>
                                                <small>Válidas</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-danger">{importPreview.errorRows}</h4>
                                                <small>Con error</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-primary">{importPreview.createRows}</h4>
                                                <small>Nuevos</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-warning">{importPreview.updateRows}</h4>
                                                <small>Actualizan</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4>{importPreview.logId}</h4>
                                                <small>Log ID</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {importPreview.confirmed && (
                                    <div className="alert alert-success">
                                        Importación confirmada correctamente. Creados:{" "}
                                        {importPreview.confirmResult?.created || 0}, actualizados:{" "}
                                        {importPreview.confirmResult?.updated || 0}, errores:{" "}
                                        {importPreview.confirmResult?.errors || 0}.
                                    </div>
                                )}

                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="mb-0">Preview de carga</h6>
                                            <small className="text-muted">
                                                Las filas con error serán omitidas al confirmar.
                                            </small>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={handleConfirmImport}
                                            disabled={
                                                confirmLoading ||
                                                importPreview.confirmed ||
                                                !importPreview.validRows
                                            }
                                        >
                                            {confirmLoading ? "Confirmando..." : "Confirmar importación"}
                                        </button>
                                    </div>

                                    <div className="card-body px-0">
                                        <div className="table-responsive">
                                            <table className="table table-striped mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Fila</th>
                                                        <th>Estado</th>
                                                        <th>Acción</th>
                                                        <th>Nombre</th>
                                                        <th>Correo</th>
                                                        <th>Distribuidor</th>
                                                        <th>Rol</th>
                                                        <th>Grado</th>
                                                        <th>Plan</th>
                                                        <th>Fecha</th>
                                                        <th>Mensaje</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {importPreview.rows?.map((row) => (
                                                        <tr key={row.rowNumber}>
                                                            <td>{row.rowNumber}</td>

                                                            <td>
                                                                {row.status === "valid" ? (
                                                                    <span className="badge bg-success-subtle text-success p-2">
                                                                        Válida
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-danger-subtle text-danger p-2">
                                                                        Error
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                {row.action === "create" && (
                                                                    <span className="badge bg-primary-subtle text-primary p-2">
                                                                        Crear
                                                                    </span>
                                                                )}

                                                                {row.action === "update" && (
                                                                    <span className="badge bg-warning-subtle text-warning p-2">
                                                                        Actualizar
                                                                    </span>
                                                                )}

                                                                {row.action === "error" && (
                                                                    <span className="badge bg-danger-subtle text-danger p-2">
                                                                        Omitir
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                {row.data?.nombre} {row.data?.apellido}
                                                            </td>

                                                            <td>{row.data?.correo || "Sin correo"}</td>

                                                            <td>{row.data?.distyNombre || "Sin disty"}</td>

                                                            <td>{row.data?.rolNombre || "Sin rol"}</td>

                                                            <td>{row.data?.gradoNombre || "Sin grado"}</td>

                                                            <td>{row.data?.Plan_Nombre || "Sin plan"}</td>

                                                            <td>{row.data?.fechaRegistro || "Fecha automática"}</td>

                                                            <td style={{ minWidth: "300px" }}>
                                                                {row.status === "invalid" ? (
                                                                    <ul className="mb-0 ps-3 text-danger">
                                                                        {row.errors?.map((err, index) => (
                                                                            <li key={index}>{err}</li>
                                                                        ))}
                                                                    </ul>
                                                                ) : (
                                                                    <span>{row.message}</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleResetImportModal}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        {/* Modal para carga Masiva de Puntos Extra */}
        <div
            className="modal fade"
            id="addPuntosModal"
            tabIndex="-1"
            aria-labelledby="addPuntosModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-fullscreen">
                <div className="modal-content">
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title" id="addPuntosModalLabel">
                                Carga Masiva de Puntos Extra
                            </h5>
                            <small className="text-muted">
                                Sube una plantilla, valida los registros y confirma la importación.
                            </small>
                        </div>

                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleResetPointsImportModal}
                        ></button>
                    </div>

                    <div className="modal-body">
                        {pointsImportError && (
                            <div className="alert alert-danger d-flex justify-content-between align-items-center">
                                <span>{pointsImportError}</span>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setPointsImportError("")}
                                ></button>
                            </div>
                        )}

                        <div className="row g-3 mb-4">
                            <div className="col-lg-3">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-2">1. Descargar plantilla</h6>
                                        <p className="text-muted mb-3">
                                            Descarga el Excel con las columnas necesarias.
                                        </p>

                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={handleDownloadPointsTemplate}
                                            disabled={downloadingPointsTemplate}
                                        >
                                            {downloadingPointsTemplate
                                                ? "Descargando..."
                                                : "Descargar plantilla"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-2">2. Configuración</h6>

                                        <label className="form-label small mb-1">
                                            Región
                                        </label>

                                        <select
                                            className="form-select form-select-sm mb-3"
                                            value={pointsRegionId}
                                            onChange={(e) => {
                                                setPointsRegionId(e.target.value);
                                                setPointsImportPreview(null);
                                                setPointsImportResult(null);
                                            }}
                                        >
                                            <option value="">Selecciona región</option>
                                            <option value="1">México</option>
                                            <option value="2">Latinoamérica</option>
                                        </select>

                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="pointsApplyCoinsSwitch"
                                                checked={pointsApplyCoins}
                                                onChange={(e) => {
                                                    setPointsApplyCoins(e.target.checked);
                                                    setPointsImportPreview(null);
                                                    setPointsImportResult(null);
                                                }}
                                            />

                                            <label
                                                className="form-check-label"
                                                htmlFor="pointsApplyCoinsSwitch"
                                            >
                                                Aplicar también Coins
                                            </label>
                                        </div>

                                        <small className="text-muted d-block mt-2">
                                            Si está activo, también se registrará en la tabla Creditos.
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-2">3. Subir archivo</h6>
                                        <p className="text-muted mb-3">
                                            Selecciona el Excel con los puntos extra.
                                        </p>

                                        <input
                                            type="file"
                                            className="form-control form-control-sm"
                                            accept=".xlsx,.xls"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0] || null;

                                                setPointsExcelFile(file);
                                                setPointsImportPreview(null);
                                                setPointsImportResult(null);
                                                setPointsImportError("");
                                            }}
                                        />

                                        {pointsExcelFile && (
                                            <small className="text-success d-block mt-2">
                                                Archivo seleccionado: {pointsExcelFile.name}
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <h6 className="mb-2">4. Validar información</h6>
                                        <p className="text-muted mb-3">
                                            Genera el preview antes de insertar los puntos.
                                        </p>

                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            onClick={handlePreviewPointsImport}
                                            disabled={
                                                previewingPointsImport ||
                                                !pointsExcelFile ||
                                                !pointsRegionId
                                            }
                                        >
                                            {previewingPointsImport
                                                ? "Validando..."
                                                : "Validar Excel"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {pointsImportPreview && (
                            <>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4>{pointsImportPreview.totalRows}</h4>
                                                <small>Total filas</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-success">
                                                    {pointsImportPreview.validRows}
                                                </h4>
                                                <small>Válidas</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-danger">
                                                    {pointsImportPreview.errorRows}
                                                </h4>
                                                <small>Con error</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-primary">
                                                    {pointsImportPreview.puntosPorInsertar}
                                                </h4>
                                                <small>Puntos</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4 className="text-warning">
                                                    {pointsImportPreview.creditosPorInsertar}
                                                </h4>
                                                <small>Coins</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <h4>{pointsImportPreview.logId}</h4>
                                                <small>Log ID</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {pointsImportResult && (
                                    <div className="alert alert-success">
                                        Importación confirmada correctamente. Puntos insertados:{" "}
                                        <strong>{pointsImportResult.puntosInsertados}</strong>. Coins insertados:{" "}
                                        <strong>{pointsImportResult.creditosInsertados}</strong>. Errores omitidos:{" "}
                                        <strong>{pointsImportResult.errores}</strong>.
                                    </div>
                                )}

                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="mb-0">Preview de carga</h6>
                                            <small className="text-muted">
                                                Las filas con error serán omitidas al confirmar.
                                            </small>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={handleCancelPointsImport}
                                                disabled={cancelingPointsImport || confirmingPointsImport}
                                            >
                                                {cancelingPointsImport
                                                    ? "Cancelando..."
                                                    : "Cancelar preview"}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={handleConfirmPointsImport}
                                                disabled={
                                                    confirmingPointsImport ||
                                                    pointsImportPreview.validRows <= 0
                                                }
                                            >
                                                {confirmingPointsImport
                                                    ? "Confirmando..."
                                                    : "Confirmar importación"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="card-body px-0">
                                        <div className="table-responsive">
                                            <table className="table table-striped mb-0 align-middle">
                                                <thead>
                                                    <tr>
                                                        <th>Fila</th>
                                                        <th>Estado</th>
                                                        <th>Acción</th>
                                                        <th>Usuario</th>
                                                        <th>Correo</th>
                                                        <th>Distribuidor</th>
                                                        {/* <th>Región</th>
                                                        <th>Plan</th>
                                                        <th>Grado</th> */}
                                                        <th>Puntos</th>
                                                        <th>Categoría</th>
                                                        <th>Descripción</th>
                                                        <th>Mensaje</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {pointsImportPreview.rows?.map((row) => (
                                                        <tr key={row.rowNumber}>
                                                            <td>{row.rowNumber}</td>

                                                            <td>
                                                                {row.status === "valid" ? (
                                                                    <span className="badge bg-success-subtle text-success p-2">
                                                                        Válida
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-danger-subtle text-danger p-2">
                                                                        Error
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                {row.action === "create" && (
                                                                    <span className="badge bg-primary-subtle text-primary p-2">
                                                                        Agregar
                                                                    </span>
                                                                )}

                                                                {row.action === "error" && (
                                                                    <span className="badge bg-danger-subtle text-danger p-2">
                                                                        Omitir
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                {row.data?.Usuario_Nombre || ""}{" "}
                                                                {row.data?.Usuario_Apellido || ""}
                                                            </td>

                                                            <td>{row.data?.correo || "Sin correo"}</td>

                                                            <td>
                                                                {row.data?.distribuidor || "Sin distribuidor"}
                                                            </td>

                                                            {/* <td>{row.data?.Region_ID || "Sin región"}</td>

                                                            <td>{row.data?.Plan_ID || "Sin plan"}</td>

                                                            <td>{row.data?.Grado_ID || "Sin grado"}</td> */}

                                                            <td>
                                                                <strong>{row.data?.puntos || 0}</strong>
                                                            </td>

                                                            <td>{row.data?.categoria || "EXTRA"}</td>

                                                            <td style={{ minWidth: "220px" }}>
                                                                {row.data?.descripcion || "Sin descripción"}
                                                            </td>

                                                            <td style={{ minWidth: "320px" }}>
                                                                {row.status === "invalid" ? (
                                                                    <ul className="mb-0 ps-3 text-danger">
                                                                        {row.errors?.map((err, index) => (
                                                                            <li key={index}>{err}</li>
                                                                        ))}
                                                                    </ul>
                                                                ) : (
                                                                    <span>{row.message}</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleResetPointsImportModal}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>


        </>
    )
}

export default UserList